import type { Settlement } from '../engine/map/settlement';
import { createToolItem, recordConsumed, recordProduced } from '../engine/map/settlement';
import { getGameConfig } from '../config/gameConfig';
import { getBuildingStorage, getEffectiveOutputCap } from '../engine/settlement/utils';

function getToolTotal(settlement: Settlement): number {
    const storage = getBuildingStorage(settlement, 'blacksmith');
    return (
        settlement.tools.farmers.length +
        settlement.tools.hunters.length +
        settlement.inventory.tools.length +
        storage.tools.length
    );
}

function getWeaponTotal(settlement: Settlement): number {
    const storage = getBuildingStorage(settlement, 'blacksmith');
    return settlement.inventory.weapons + storage.weapons;
}

function canStoreTool(settlement: Settlement): boolean {
    const config = getGameConfig();
    const cap = getEffectiveOutputCap(settlement, config, 'blacksmith', 'tools');
    if (cap <= 0) return false;
    const storage = getBuildingStorage(settlement, 'blacksmith');
    return cap - storage.tools.length >= config.production.blacksmith.toolsPerBatch;
}

function canStoreWeapon(settlement: Settlement): boolean {
    const config = getGameConfig();
    const cap = getEffectiveOutputCap(settlement, config, 'blacksmith', 'weapons');
    if (cap <= 0) return false;
    const storage = getBuildingStorage(settlement, 'blacksmith');
    return cap - storage.weapons >= config.production.blacksmith.weaponsPerBatch;
}

function canCraftTool(settlement: Settlement): boolean {
    const config = getGameConfig().production.blacksmith;
    const storage = getBuildingStorage(settlement, 'blacksmith');
    return storage.ironIngots >= config.toolIngots && storage.wood >= config.toolWood;
}

function canCraftWeapon(settlement: Settlement): boolean {
    const config = getGameConfig().production.blacksmith;
    const storage = getBuildingStorage(settlement, 'blacksmith');
    return (
        storage.ironIngots >= config.weaponIngots &&
        storage.wood >= config.weaponWood &&
        storage.leather >= config.weaponLeather
    );
}

function pickTask(settlement: Settlement): 'tool' | 'weapon' | null {
    const toolMissing = Math.max(
        0,
        settlement.production.blacksmith.targetTools - getToolTotal(settlement),
    );
    const weaponMissing = Math.max(
        0,
        settlement.production.blacksmith.targetWeapons - getWeaponTotal(settlement),
    );
    const toolAvailable = toolMissing > 0 && canStoreTool(settlement);
    const weaponAvailable = weaponMissing > 0 && canStoreWeapon(settlement);
    if (!toolAvailable && !weaponAvailable) return null;
    const preferTool = toolMissing >= weaponMissing;
    if (preferTool) {
        if (toolAvailable && canCraftTool(settlement)) return 'tool';
        if (weaponAvailable && canCraftWeapon(settlement)) return 'weapon';
    } else {
        if (weaponAvailable && canCraftWeapon(settlement)) return 'weapon';
        if (toolAvailable && canCraftTool(settlement)) return 'tool';
    }
    return null;
}

export function runBlacksmithTick(settlement: Settlement): boolean {
    if (settlement.buildings.blacksmith.qty <= 0 || settlement.jobs.smiths <= 0) {
        const hadWork =
            settlement.production.blacksmith.progress.length > 0 ||
            settlement.production.blacksmith.task.length > 0;
        settlement.production.blacksmith.progress = [];
        settlement.production.blacksmith.task = [];
        return hadWork;
    }

    let changed = false;
    while (settlement.production.blacksmith.progress.length < settlement.jobs.smiths) {
        settlement.production.blacksmith.progress.push(0);
        settlement.production.blacksmith.task.push('tool');
        changed = true;
    }
    while (settlement.production.blacksmith.progress.length > settlement.jobs.smiths) {
        settlement.production.blacksmith.progress.pop();
        settlement.production.blacksmith.task.pop();
        changed = true;
    }

    const config = getGameConfig().production.blacksmith;
    const storage = getBuildingStorage(settlement, 'blacksmith');
    for (let i = 0; i < settlement.production.blacksmith.progress.length; i += 1) {
        if (settlement.production.blacksmith.progress[i] === 0) {
            const nextTask = pickTask(settlement);
            if (!nextTask) continue;
            settlement.production.blacksmith.task[i] = nextTask;
        }

        const currentTask = settlement.production.blacksmith.task[i];
        const canCraft =
            currentTask === 'tool' ? canCraftTool(settlement) : canCraftWeapon(settlement);
        if (!canCraft && settlement.production.blacksmith.progress[i] === 0) {
            const fallback = pickTask(settlement);
            if (!fallback) continue;
            settlement.production.blacksmith.task[i] = fallback;
        }
        const taskReady = settlement.production.blacksmith.task[i];
        if (taskReady === 'tool' && (!canCraftTool(settlement) || !canStoreTool(settlement)))
            continue;
        if (taskReady === 'weapon' && (!canCraftWeapon(settlement) || !canStoreWeapon(settlement)))
            continue;

        const requiredTicks = taskReady === 'tool' ? config.toolTicks : config.weaponTicks;
        settlement.production.blacksmith.progress[i] += 1;
        if (settlement.production.blacksmith.progress[i] < requiredTicks) continue;

        settlement.production.blacksmith.progress[i] -= requiredTicks;
        if (taskReady === 'tool') {
            storage.ironIngots = Math.max(0, storage.ironIngots - config.toolIngots);
            storage.wood = Math.max(0, storage.wood - config.toolWood);
            const toolCap = getEffectiveOutputCap(
                settlement,
                getGameConfig(),
                'blacksmith',
                'tools',
            );
            const toolSpace = Math.max(0, toolCap - storage.tools.length);
            const toolsProduced = Math.min(config.toolsPerBatch, toolSpace);
            if (toolsProduced > 0) {
                const tools = Array.from({ length: toolsProduced }, () =>
                    createToolItem(settlement),
                );
                storage.tools.push(...tools);
                recordProduced(settlement, 'tools', toolsProduced);
            }
            recordConsumed(settlement, 'ironIngots', config.toolIngots);
            recordConsumed(settlement, 'wood', config.toolWood);
        } else {
            storage.ironIngots = Math.max(0, storage.ironIngots - config.weaponIngots);
            storage.wood = Math.max(0, storage.wood - config.weaponWood);
            storage.leather = Math.max(0, storage.leather - config.weaponLeather);
            const weaponCap = getEffectiveOutputCap(
                settlement,
                getGameConfig(),
                'blacksmith',
                'weapons',
            );
            const weaponSpace = Math.max(0, weaponCap - storage.weapons);
            const weaponsProduced = Math.min(config.weaponsPerBatch, weaponSpace);
            if (weaponsProduced > 0) {
                storage.weapons += weaponsProduced;
                recordProduced(settlement, 'weapons', weaponsProduced);
            }
            recordConsumed(settlement, 'ironIngots', config.weaponIngots);
            recordConsumed(settlement, 'wood', config.weaponWood);
            recordConsumed(settlement, 'leather', config.weaponLeather);
        }
        changed = true;
    }

    return changed;
}
